import { create } from 'zustand';
import useEditorStore from '../useEditorStore';
import usePreparingScanStore from '../usePreparingScanStore';
import * as THREE from 'three';
import { computeBoundsTree } from 'three-mesh-bvh';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'

const useSliceStore = create((set, get) => ({
  // --- STATE ---
  slicingPlane: null,       // The active THREE.Plane for slicing
  activeSlice: null,        // Holds { upper: Mesh, lower: Mesh } during preview
  selectionDirection: 'upper', // 'upper' or 'lower'
  geometryHistory: [],      // An array of BufferGeometries for the undo stack

  // --- ACTIONS ---

  /** Starts the slice preview process. */
  startSlicePreview: (plane) => {
    set({ slicingPlane: plane, selectionDirection: 'upper' });
  },

  /** Updates the slicing plane without resetting the selection direction. */
  updateSlicingPlane: (plane) => {
    set({ slicingPlane: plane });
  },

  /** Stores the generated slice pieces for the preview. */
  setActiveSlicePieces: (pieces) => {
    set({ activeSlice: pieces });
  },

  /** Toggles the selection between the upper and lower mesh parts. */
  invertSelection: () => {
    set((state) => ({
      selectionDirection: state.selectionDirection === 'upper' ? 'lower' : 'upper',
    }));
  },

  /** Finalizes the trim, discarding the unselected part. */
  createTrimCommand: () => {
    const { activeSlice, selectionDirection } = get();
    const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
    const { clearSelection } = usePreparingScanStore.getState();
    const originalMesh = sceneRefs.shadedMesh;

    if (!activeSlice || !originalMesh) return null; // Return null if command can't be created

    // --- Capture the "BEFORE" state for the undo function ---
    // Clone geometry because replaceShadedMesh disposes the old geometry
    const originalGeometry = originalMesh.geometry;
    const originalGeometryClone = new THREE.BufferGeometry().copy(originalGeometry);
    const originalMaterial = originalMesh.material;
    const originalMatrix = originalMesh.matrix.clone();

    // --- RETURN the command object ---
    return {
      execute: () => {
        const meshToKeep = activeSlice[selectionDirection];
        const newMesh = new THREE.Mesh(meshToKeep.geometry, originalMaterial);

        // **CRITICAL FIX**: Preserve original matrixAutoUpdate state
        // Keep the same matrix handling as the original mesh for consistency
        newMesh.matrix.copy(originalMatrix);
        newMesh.matrixAutoUpdate = originalMesh.matrixAutoUpdate;

        // If matrixAutoUpdate is enabled, decompose the matrix to position/rotation/scale
        if (newMesh.matrixAutoUpdate) {
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
        }

        // **CRITICAL FIX**: Ensure all spatial structures are properly initialized
        // Geometry is already INDEXED with properly merged vertices from worker
        // Normals are already correctly computed on the merged geometry
        if (!newMesh.geometry.attributes.normal) {
          newMesh.geometry.computeVertexNormals();
        }
        newMesh.geometry.computeBoundingBox();
        newMesh.geometry.computeBoundingSphere();

        // Build BVH for raycasting operations
        computeBoundsTree.call(newMesh.geometry);

        replaceShadedMesh(newMesh);
        get().clearSlicePreview();
        clearSelection();
      },
      undo: () => {
        const restoredMesh = new THREE.Mesh(originalGeometryClone, originalMaterial);

        // **CRITICAL FIX**: Restore original matrixAutoUpdate state
        restoredMesh.matrix.copy(originalMatrix);
        restoredMesh.matrixAutoUpdate = originalMesh.matrixAutoUpdate;

        // If matrixAutoUpdate is enabled, decompose the matrix
        if (restoredMesh.matrixAutoUpdate) {
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
        }

        // **CRITICAL FIX**: Ensure spatial structures are intact on restored mesh
        // The cloned geometry should have these, but ensure BVH is disposed if stale
        if (originalGeometryClone.boundsTree) {
          // Dispose and let it rebuild fresh if needed
          originalGeometryClone.disposeBoundsTree();
        }

        replaceShadedMesh(restoredMesh);
      }
    };
  },

  /** Cancels the slice preview and restores the original view. */
  clearSlicePreview: (shouldClearHistory = true) => {
    set({
      slicingPlane: null,
      activeSlice: null,
      geometryHistory: shouldClearHistory ? [] : get().geometryHistory,
    });
  },
}));

export default useSliceStore;